import { Box, styled } from "@mui/material";
import { Clear } from "@procore/core-icons";
import { Button, Search, Spinner, Typography } from "@procore/core-react";
import { vec2 } from "gl-matrix";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { TOOLBAR_Z_INDEX } from "components/toolbars";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CameraType, renderActions } from "features/render";
import { useOverlayZIndex } from "hooks/useOverlayZIndex";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";

import { ControlBarWeb } from "./ControlBarWeb";
import { LevelsTreeWeb } from "./levelsTreeWeb";
import {
    selectCurrentDrawing,
    selectMiniMapDrawingIds,
    selectTempDrawing,
    selectTempLevel,
    setCurrentDrawing,
    setDrawingData,
    setTempDrawingId,
} from "./minimapSlice";
import {
    calculateImageFitBox,
    drawingToModelSpace,
    drawLargeMapPositionIndicator,
    EYE_ELEVATION_OFFSET,
    fetchDrawingData,
    modelToDrawingSpace,
} from "./miniMapUtils";
import type { DrawingSpacePosition } from "./types";

// Margins for image positioning: 24px from top/left/right, 24px + control bar height from bottom
const IMAGE_MARGIN = Object.freeze({
    top: 24,
    right: 24,
    bottom: 24 + 50 + 24, // 24px padding + ~50px control bar + 24px bottom margin
    left: 24,
});

const Overlay = styled(Box)<{ open: boolean }>(({ open }) => ({
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    opacity: open ? 1 : 0,
    visibility: open ? "visible" : "hidden",
    transition: "opacity 300ms ease-out, visibility 300ms ease-out",
    pointerEvents: open ? "auto" : "none",
}));

const TearSheet = styled(Box)<{ open: boolean }>(({ open }) => ({
    position: "fixed",
    top: "68px",
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#fff",
    boxShadow: "0px -4px 20px rgba(0, 0, 0, 0.15)",
    transform: open ? "translateY(0)" : "translateY(100%)",
    transition: "transform 400ms cubic-bezier(0.4, 0, 0.2, 1)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    pointerEvents: "auto",
}));

const TearSheetContent = styled(Box)({
    display: "flex",
    height: "100%",
    width: "100%",
    overflow: "hidden",
    position: "relative",
});

const LeftPanel = styled(Box)({
    width: "25%",
    borderRight: "1px solid rgba(214, 218, 220, 1)",
    boxShadow: "4px 0px 12px 0px rgba(0, 0, 0, 0.2)",
    backgroundColor: "rgba(255, 255, 255, 1)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minWidth: 300,
});

const RightPanel = styled(Box)({
    width: "75%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fafafa",
    overflow: "hidden",
    minWidth: 300,
});

const LoadingOverlay = styled(Box)({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    gap: 16,
});

interface LargeMapWebProps {
    open: boolean;
    onClose: () => void;
}

export function LargeMapWeb({ open, onClose }: LargeMapWebProps) {
    const largeCanvasRef = useRef<HTMLCanvasElement>(null);
    const rightPanelRef = useRef<HTMLDivElement>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [canvasSize, setCanvasSize] = useState([800, 600]);
    const [placedPosition, setPlacedPosition] = useState<DrawingSpacePosition | null>(null);
    const imageFitBoxRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
    const [scale, setScale] = useState(1);
    const [panOffset, setPanOffset] = useState<vec2>(vec2.fromValues(0, 0));
    const isPanningRef = useRef(false);
    const lastPanPosRef = useRef<vec2>(vec2.fromValues(0, 0));
    const drawingIds = useAppSelector(selectMiniMapDrawingIds);
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const tempDrawing = useAppSelector(selectTempDrawing);
    const tempLevel = useAppSelector(selectTempLevel);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const {
        state: { view },
    } = useExplorerGlobals();

    useEffect(() => {
        if (open) {
            dispatch(setTempDrawingId(currentDrawing?.id ?? null));
        }
        // Only run when open changes, not when currentDrawing changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dispatch]);

    const handleClose = useCallback(() => {
        setPlacedPosition(null);
        setScale(1);
        setPanOffset(vec2.fromValues(0, 0));
        dispatch(setTempDrawingId(null));
        onClose();
    }, [onClose, dispatch]);

    const totalPages = drawingIds.length;
    const currentPageIndex = tempDrawing ? drawingIds.indexOf(tempDrawing.id) : -1;

    const handlePrevPage = useCallback(() => {
        if (totalPages === 0) {
            return;
        }
        const prevIndex = currentPageIndex <= 0 ? totalPages - 1 : currentPageIndex - 1;
        dispatch(setTempDrawingId(drawingIds[prevIndex]));
        setScale(1);
        setPanOffset(vec2.fromValues(0, 0));
    }, [currentPageIndex, totalPages, drawingIds, dispatch]);

    const handleNextPage = useCallback(() => {
        if (totalPages === 0) {
            return;
        }
        const nextIndex = currentPageIndex >= totalPages - 1 ? 0 : currentPageIndex + 1;
        dispatch(setTempDrawingId(drawingIds[nextIndex]));
        setScale(1);
        setPanOffset(vec2.fromValues(0, 0));
    }, [currentPageIndex, totalPages, drawingIds, dispatch]);

    const handleZoomIn = useCallback(() => {
        const zoomFactor = 1.1;
        setScale((prev) => Math.min(10, prev * zoomFactor));
        setPanOffset((prev) => vec2.fromValues(prev[0] * zoomFactor, prev[1] * zoomFactor));
    }, []);

    const handleZoomOut = useCallback(() => {
        const zoomFactor = 1 / 1.1;
        setScale((prev) => Math.max(0.1, prev * zoomFactor));
        setPanOffset((prev) => vec2.fromValues(prev[0] * zoomFactor, prev[1] * zoomFactor));
    }, []);

    const handleZoomToValue = useCallback((newScale: number) => {
        setScale((prevScale) => {
            const zoomFactor = newScale / prevScale;
            setPanOffset((prev) => vec2.fromValues(prev[0] * zoomFactor, prev[1] * zoomFactor));
            return newScale;
        });
    }, []);

    // Keyboard navigation for left/right arrows
    useEffect(() => {
        if (!open) {
            return;
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                e.preventDefault();
                e.stopPropagation();
                handlePrevPage();
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                e.stopPropagation();
                handleNextPage();
            }
        };

        window.addEventListener("keydown", handleKeyDown, { capture: true });

        return () => {
            window.removeEventListener("keydown", handleKeyDown, { capture: true });
        };
    }, [open, handlePrevPage, handleNextPage]);

    // Measure RightPanel dimensions and update canvas size (minus padding)
    useEffect(() => {
        if (!open || !rightPanelRef.current) {
            return;
        }

        const updateCanvasSize = () => {
            const panel = rightPanelRef.current;
            if (panel) {
                const rect = panel.getBoundingClientRect();
                const computedStyle = window.getComputedStyle(panel);
                const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
                const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
                const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
                const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
                const availableWidth = Math.max(1, rect.width - (paddingLeft + paddingRight));
                const availableHeight = Math.max(1, rect.height - (paddingTop + paddingBottom));
                setCanvasSize([availableWidth, availableHeight]);
            }
        };

        // Initial measurement
        updateCanvasSize();

        // Update on resize
        const resizeObserver = new ResizeObserver(updateCanvasSize);
        resizeObserver.observe(rightPanelRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [open]);

    const drawLargeMap = useCallback(
        (
            ctx: CanvasRenderingContext2D,
            img: ImageBitmap,
            canvasWidth: number,
            canvasHeight: number,
            placed: DrawingSpacePosition | null,
            currentScale: number,
            currentPanOffset: vec2,
        ) => {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);

            const fitBox = calculateImageFitBox({
                imageDimensions: [img.width, img.height],
                canvasDimensions: [canvasWidth, canvasHeight],
                scale: currentScale,
                panOffset: currentPanOffset,
                margin: IMAGE_MARGIN,
            });
            imageFitBoxRef.current = fitBox;
            const { width, height, x, y } = fitBox;

            // Draw shadow around image (similar to box-shadow: 0px 0px 20px 0px #00000033)
            ctx.save();
            ctx.shadowColor = "rgba(0, 0, 0, 0.2)";
            ctx.shadowBlur = 20;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            ctx.fillStyle = "white";
            ctx.fillRect(x, y, width, height);
            ctx.restore();

            // Draw the image on top
            ctx.drawImage(img, x, y, width, height);

            if (placed) {
                const posX = x + placed.position[0] * width;
                const posY = y + placed.position[1] * height;
                drawLargeMapPositionIndicator(ctx, posX, posY, placed.rotation);
            } else if (view && tempDrawing?.modelAB && tempDrawing?.drawingAB) {
                const camera = view.renderState.camera;
                const position = modelToDrawingSpace(tempDrawing.modelAB, tempDrawing.drawingAB, camera);
                const posX = x + position.position[0] * width;
                const posY = y + position.position[1] * height;
                drawLargeMapPositionIndicator(ctx, posX, posY, position.rotation);
            }
        },
        [view, tempDrawing],
    );

    const calculateRotationToMouse = useCallback((indicatorPos: vec2, mouseX: number, mouseY: number): number => {
        const fitBox = imageFitBoxRef.current;
        if (!fitBox) {
            return 0;
        }

        const indicatorCanvasX = fitBox.x + indicatorPos[0] * fitBox.width;
        const indicatorCanvasY = fitBox.y + indicatorPos[1] * fitBox.height;

        const dx = mouseX - indicatorCanvasX;
        const dy = mouseY - indicatorCanvasY;

        const angleRad = Math.atan2(dx, -dy);
        let angleDeg = angleRad * (180 / Math.PI);

        if (angleDeg < 0) {
            angleDeg += 360;
        }

        return angleDeg;
    }, []);

    const handleCanvasClick = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            const canvas = largeCanvasRef.current;
            const fitBox = imageFitBoxRef.current;
            if (!canvas || !fitBox || !tempDrawing?.modelAB || !tempDrawing?.drawingAB) {
                return;
            }

            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            if (
                mouseX < fitBox.x ||
                mouseX > fitBox.x + fitBox.width ||
                mouseY < fitBox.y ||
                mouseY > fitBox.y + fitBox.height
            ) {
                return;
            }

            if (!placedPosition) {
                const percentX = (mouseX - fitBox.x) / fitBox.width;
                const percentY = (mouseY - fitBox.y) / fitBox.height;

                setPlacedPosition({
                    position: vec2.fromValues(percentX, percentY),
                    rotation: 0,
                });
            } else if (view && tempLevel) {
                const elevation = tempLevel.elevation + EYE_ELEVATION_OFFSET;
                const currentRotation = view.renderState.camera.rotation;

                const modelSpacePos = drawingToModelSpace(
                    placedPosition,
                    tempDrawing.modelAB,
                    tempDrawing.drawingAB,
                    elevation,
                    currentRotation,
                );

                dispatch(
                    renderActions.setCamera({
                        type: CameraType.Pinhole,
                        goTo: {
                            position: modelSpacePos.position,
                            rotation: modelSpacePos.rotation,
                            flyTime: 1000,
                        },
                    }),
                );

                // Commit the temp drawing as the current drawing
                dispatch(setCurrentDrawing(tempDrawing.id));
                handleClose();
            }
        },
        [placedPosition, tempDrawing, tempLevel, view, handleClose, dispatch],
    );

    const handleCanvasMouseMove = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            const canvas = largeCanvasRef.current;
            if (!canvas) {
                return;
            }

            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            // Handle panning
            if (isPanningRef.current) {
                const dx = mouseX - lastPanPosRef.current[0];
                const dy = mouseY - lastPanPosRef.current[1];
                setPanOffset((prev) => vec2.fromValues(prev[0] + dx, prev[1] + dy));
                lastPanPosRef.current = vec2.fromValues(mouseX, mouseY);
                return;
            }

            // Handle rotation for placed position
            if (placedPosition) {
                const rotation = calculateRotationToMouse(placedPosition.position, mouseX, mouseY);
                setPlacedPosition((prev) => {
                    if (!prev) {
                        return null;
                    }
                    return {
                        ...prev,
                        rotation,
                    };
                });
            }
        },
        [placedPosition, calculateRotationToMouse],
    );

    const handleCanvasMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (e.button === 1) {
            e.preventDefault();
            const canvas = largeCanvasRef.current;
            if (!canvas) {
                return;
            }
            const rect = canvas.getBoundingClientRect();
            isPanningRef.current = true;
            lastPanPosRef.current = vec2.fromValues(e.clientX - rect.left, e.clientY - rect.top);
        }
    }, []);

    const handleCanvasMouseUp = useCallback(() => {
        isPanningRef.current = false;
    }, []);

    useEffect(() => {
        const canvas = largeCanvasRef.current;
        if (!canvas || !open) {
            return;
        }

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();

            const fitBox = imageFitBoxRef.current;
            if (!fitBox) {
                return;
            }

            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            // Calculate zoom factor (10% per wheel tick), max 10x
            const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
            const newScale = Math.max(0.1, Math.min(10, scale * zoomFactor));

            // Calculate the mouse position relative to the image center before zoom
            const imageCenterX = fitBox.x + fitBox.width / 2;
            const imageCenterY = fitBox.y + fitBox.height / 2;
            const mouseOffsetX = mouseX - imageCenterX;
            const mouseOffsetY = mouseY - imageCenterY;

            // Adjust pan offset to keep mouse position fixed
            const scaleRatio = newScale / scale;
            const newPanOffsetX = panOffset[0] - mouseOffsetX * (scaleRatio - 1);
            const newPanOffsetY = panOffset[1] - mouseOffsetY * (scaleRatio - 1);

            setScale(newScale);
            setPanOffset(vec2.fromValues(newPanOffsetX, newPanOffsetY));
        };

        canvas.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            canvas.removeEventListener("wheel", handleWheel);
        };
    }, [open, scale, panOffset]);

    // Canvas setup when sheet opens
    useEffect(() => {
        if (!open) {
            return;
        }

        const canvas = largeCanvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;
        const [canvasWidth, canvasHeight] = canvasSize;

        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        canvas.style.width = `${canvasWidth}px`;
        canvas.style.height = `${canvasHeight}px`;
        ctx.scale(dpr, dpr);

        if (!tempDrawing) {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            return;
        }

        const { status } = tempDrawing.drawingData.asyncState;
        switch (status) {
            case AsyncStatus.Initial:
                dispatch(setDrawingData({ drawingId: tempDrawing.id, asyncState: { status: AsyncStatus.Loading } }));
                fetchDrawingData(tempDrawing.drawingData.url)
                    .then((imgBitmap) => {
                        dispatch(
                            setDrawingData({
                                drawingId: tempDrawing.id,
                                asyncState: { status: AsyncStatus.Success, data: imgBitmap },
                            }),
                        );
                    })
                    .catch((error) => {
                        const e = error as Error;
                        telemetry.trackCaughtException({
                            source: "miniMap.largeMapWeb.fetchDrawingData",
                            category: "render",
                            error,
                            extraAttributes: { drawingId: tempDrawing.id },
                        });
                        dispatch(
                            setDrawingData({
                                drawingId: tempDrawing.id,
                                asyncState: { status: AsyncStatus.Error, msg: e.message },
                            }),
                        );
                    });
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                break;
            case AsyncStatus.Loading:
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                break;
            case AsyncStatus.Success:
                drawLargeMap(
                    ctx,
                    tempDrawing.drawingData.asyncState.data,
                    canvasWidth,
                    canvasHeight,
                    placedPosition,
                    scale,
                    panOffset,
                );
                break;
            case AsyncStatus.Error:
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                break;
            default:
        }
    }, [open, tempDrawing, canvasSize, drawLargeMap, placedPosition, scale, panOffset, dispatch]);

    const overlayZIndex = useOverlayZIndex();
    const zIndex = Math.max(overlayZIndex, TOOLBAR_Z_INDEX.LARGE_MAP_WEB);

    return createPortal(
        <div
            style={{
                position: "fixed",
                inset: 0,
                pointerEvents: "none",
                zIndex,
            }}
        >
            <Overlay open={open} onClick={handleClose} />
            <TearSheet open={open}>
                <TearSheetContent>
                    <LeftPanel>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                                borderBottom: "1px solid rgba(214, 218, 220, 1)",
                                padding: "16px 16px 8px 16px",
                                flexShrink: 0,
                            }}
                        >
                            <Typography intent="h3">{t("modelLevelsHeader")}</Typography>
                            <Typography intent="body" color={"gray45"} style={{ marginBottom: "16px" }}>
                                {t("modelLevelsHeaderSubtext")}
                            </Typography>
                            <Search
                                placeholder="Filter by Keyword"
                                color={"gray94"}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    gap: "8px",
                                    paddingTop: "8px",
                                    paddingRight: "16px",
                                    paddingBottom: "8px",
                                    paddingLeft: "16px",
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                            <LevelsTreeWeb
                                searchTerm={searchTerm}
                                onDrawingChange={() => {
                                    setScale(1);
                                    setPanOffset(vec2.fromValues(0, 0));
                                }}
                            />
                        </Box>
                    </LeftPanel>
                    <RightPanel ref={rightPanelRef}>
                        {tempDrawing && (
                            <canvas
                                ref={largeCanvasRef}
                                onClick={handleCanvasClick}
                                onMouseMove={handleCanvasMouseMove}
                                onMouseDown={handleCanvasMouseDown}
                                onMouseUp={handleCanvasMouseUp}
                                onMouseLeave={handleCanvasMouseUp}
                            />
                        )}
                        {tempDrawing &&
                            (tempDrawing.drawingData.asyncState.status === AsyncStatus.Loading ||
                                tempDrawing.drawingData.asyncState.status === AsyncStatus.Initial) && (
                                <LoadingOverlay>
                                    <Spinner size="lg" loading={true} />
                                    <Typography>{t("loadingDrawing")}</Typography>
                                </LoadingOverlay>
                            )}
                        <ControlBarWeb
                            currentPageIndex={currentPageIndex}
                            totalPages={totalPages}
                            scale={scale}
                            onPrevPage={handlePrevPage}
                            onNextPage={handleNextPage}
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                            onZoomToValue={handleZoomToValue}
                        />{" "}
                    </RightPanel>
                </TearSheetContent>
            </TearSheet>
            <Button
                onClick={handleClose}
                variant="secondary"
                style={{
                    position: "fixed",
                    top: 16,
                    right: 24,
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: open ? "translateY(0)" : "translateY(100vh)",
                    transition: "transform 400ms cubic-bezier(0.4, 0, 0.2, 1)",
                    opacity: open ? 1 : 0,
                    visibility: open ? "visible" : "hidden",
                    pointerEvents: open ? "auto" : "none",
                }}
            >
                <Clear color="rgba(35, 39, 41, 1)" style={{ marginTop: 4 }} />
            </Button>
        </div>,
        document.body,
    );
}
